import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import nativeFirestore from '@react-native-firebase/firestore';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CUENTAS DE COBRO POR TRANSFERENCIA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Antes vivían HARDCODEADAS en `wallet.tsx` y `WalletModal.tsx`. Eso trajo
 * dos problemas serios:
 *
 *   1. Cambiar una cuenta exigía recompilar la app y volver a subirla a Play
 *      Store — imposible de hacer rápido si un banco cierra una cuenta.
 *   2. Una de las cuentas quedó a nombre de un TERCERO ajeno al negocio.
 *      Google Play revisa a dónde va el dinero del usuario y lo trata como
 *      señal de fraude o suplantación. Además, `WalletModal` mostraba
 *      "Titular: Go Safely SRL" fijo para todas — información engañosa,
 *      porque eran cuentas personales.
 *
 * AHORA viven en Firestore, documento `app_config/payment_accounts`, y se
 * administran desde la CONSOLA DE FIREBASE sin tocar código ni publicar
 * versión nueva. Las reglas (`firestore.rules`) permiten lectura a cualquier
 * usuario con sesión y escritura SOLO a admin con MFA.
 *
 * ── Forma del documento en Firestore ──────────────────────────────────────
 *
 *   Colección: app_config
 *   Documento: payment_accounts
 *   Campo:     accounts   (tipo: array)
 *
 *   Cada elemento del array es un map con estos campos:
 *     id            string   identificador corto, ej: "popular"
 *     name          string   nombre visible, ej: "Banco Popular"
 *     holder        string   TITULAR REAL de la cuenta. Obligatorio.
 *     accountNumber string   número de cuenta o teléfono (para Toke)
 *     accountType   string   "Ahorros" | "Corriente" | "Teléfono" | "Efectivo"
 *     kind          string   "BANK" | "PHONE" | "CASH"
 *     enabled       boolean  false la oculta sin borrarla
 *     order         number   orden de aparición (menor primero)
 *
 * Si Firestore falla o el documento no existe, se sirve FALLBACK — así la
 * pantalla de recarga nunca queda vacía.
 */

export interface PaymentAccount {
  id: string;
  name: string;
  holder: string;
  accountNumber: string;
  accountType: string;
  kind: 'BANK' | 'PHONE' | 'CASH';
  enabled: boolean;
  order: number;
}

const CACHE_KEY = 'GOSAFELY_PAYMENT_ACCOUNTS_V1';
const TTL_MS = 60 * 60 * 1000; // 1 h — corto: si cambias una cuenta, se propaga rápido

/**
 * Respaldo mínimo. SOLO cuentas del titular de la app — nunca de terceros.
 * Si necesitas agregar, quitar o cambiar una cuenta, hazlo en Firestore,
 * no aquí. Esto existe únicamente para que la pantalla no quede vacía
 * cuando no hay red o el documento todavía no se ha creado.
 */
const FALLBACK: PaymentAccount[] = [
  {
    id: 'popular',
    name: 'Banco Popular',
    holder: 'Santo Montilla',
    accountNumber: '787258847',
    accountType: 'Ahorros',
    kind: 'BANK',
    enabled: true,
    order: 1,
  },
  {
    id: 'toke',
    name: 'Toke',
    holder: 'Santo Montilla',
    accountNumber: '8295420292',
    accountType: 'Teléfono',
    kind: 'PHONE',
    enabled: true,
    order: 2,
  },
];

interface CacheEntry {
  data: PaymentAccount[];
  fetchedAt: number;
}

let memoryCache: CacheEntry | null = null;

/** Normaliza y valida un elemento crudo de Firestore. Descarta lo inválido. */
function sanitize(raw: any, index: number): PaymentAccount | null {
  if (!raw || typeof raw !== 'object') return null;
  const accountNumber = String(raw.accountNumber ?? '').trim();
  const name = String(raw.name ?? '').trim();
  const holder = String(raw.holder ?? '').trim();
  // Sin titular no se publica: mostrar una cuenta sin dueño declarado es
  // exactamente el problema que esta migración vino a resolver.
  if (!accountNumber || !name || !holder) return null;
  const kind = raw.kind === 'PHONE' || raw.kind === 'CASH' ? raw.kind : 'BANK';
  return {
    id: String(raw.id ?? `acct_${index}`),
    name,
    holder,
    accountNumber,
    accountType: String(raw.accountType ?? (kind === 'PHONE' ? 'Teléfono' : 'Ahorros')),
    kind,
    enabled: raw.enabled !== false,
    order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : index + 1,
  };
}

async function readPersisted(): Promise<CacheEntry | null> {
  try {
    const rawStr = await AsyncStorage.getItem(CACHE_KEY);
    if (!rawStr) return null;
    const parsed = JSON.parse(rawStr);
    if (Array.isArray(parsed?.data) && typeof parsed.fetchedAt === 'number') return parsed;
    return null;
  } catch {
    return null;
  }
}

async function persist(entry: CacheEntry) {
  try {
    await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* ignore */
  }
}

/**
 * Lee la lista de Firestore.
 *
 * DISTINGUE DOS COSAS QUE ANTES ERAN LA MISMA, y esa confusión tenía una
 * consecuencia con dinero de por medio:
 *
 *   null  → NO SE PUDO LEER. No hay red, se acabó el tiempo de espera, el
 *           documento no existe todavía, o el campo no es un array. Aquí sí
 *           corresponde servir el respaldo: la pantalla no puede quedar vacía
 *           por un problema de conexión.
 *
 *   []    → SE LEYÓ BIEN Y NO HAY NINGUNA CUENTA ENCENDIDA. Es una decisión
 *           deliberada de quien administra: apagó o quitó las cuentas. Antes
 *           también devolvía null, o sea que la app seguía enseñando las
 *           cuentas incrustadas de respaldo — y el usuario transfería a una
 *           cuenta retirada mientras el servidor rechazaba su comprobante.
 *           Ahora se respeta: sin cuentas, no se ofrece transferencia.
 */
async function fetchFromFirestore(): Promise<PaymentAccount[] | null> {
  // Timeout duro: la pantalla de recarga nunca debe quedarse colgada.
  const withTimeout = <T,>(p: Promise<T>, ms = 6000) =>
    Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), ms))]);

  let data: any = null;
  if (Platform.OS !== 'web') {
    const snap: any = await withTimeout(
      nativeFirestore().collection('app_config').doc('payment_accounts').get()
    );
    if (!snap || !snap.exists || !snap.exists()) return null;
    data = snap.data();
  } else {
    const snap: any = await withTimeout(getDoc(doc(db, 'app_config', 'payment_accounts')));
    if (!snap || !snap.exists || !snap.exists()) return null;
    data = snap.data();
  }

  const rawList = Array.isArray(data?.accounts) ? data.accounts : null;
  if (!rawList) return null;

  const clean = rawList
    .map(sanitize)
    .filter((a: PaymentAccount | null): a is PaymentAccount => a !== null)
    .filter((a: PaymentAccount) => a.enabled)
    .sort((a: PaymentAccount, b: PaymentAccount) => a.order - b.order);

  // Ojo: aquí se devuelve `clean` aunque esté vacío. Ver el comentario de
  // arriba: vacío NO es lo mismo que "no se pudo leer".
  return clean;
}

export const paymentAccountsService = {
  /**
   * Cuentas activas, listas para pintar. Nunca lanza.
   *
   * SÍ puede devolver una lista VACÍA: significa que se leyó bien y quien
   * administra no dejó ninguna cuenta encendida. Quien la llame tiene que
   * pintar ese caso ("ahora mismo no se puede pagar por transferencia") en
   * vez de dar por hecho que siempre hay al menos una.
   *
   * Orden de preferencia: caché fresca → Firestore → caché vieja → FALLBACK.
   * El FALLBACK es solo para cuando NO SE PUDO LEER.
   */
  async getAccounts(forceRefresh = false): Promise<PaymentAccount[]> {
    const now = Date.now();

    if (!forceRefresh) {
      if (memoryCache && now - memoryCache.fetchedAt < TTL_MS) return memoryCache.data;
      if (!memoryCache) {
        const persisted = await readPersisted();
        if (persisted && now - persisted.fetchedAt < TTL_MS) {
          memoryCache = persisted;
          return persisted.data;
        }
      }
    }

    try {
      const fresh = await fetchFromFirestore();
      // `fresh` puede ser un array vacío y eso es una respuesta válida: hay que
      // compararlo contra null, no evaluarlo como booleano (un [] es truthy en
      // JS, pero el día que alguien "simplifique" esto a `if (fresh?.length)`
      // vuelve el fallo de servir cuentas retiradas).
      if (fresh !== null) {
        const entry: CacheEntry = { data: fresh, fetchedAt: now };
        memoryCache = entry;
        persist(entry).catch(() => {});
        return fresh;
      }
      console.warn('[paymentAccounts] doc app_config/payment_accounts vacío o ausente → fallback');
    } catch (e: any) {
      console.warn('[paymentAccounts] error leyendo Firestore:', e?.message ?? e);
    }

    if (memoryCache) return memoryCache.data;
    const persisted = await readPersisted();
    if (persisted) return persisted.data;
    return FALLBACK;
  },

  /** Respaldo, expuesto para render inicial sin await (evita parpadeo). */
  getFallback(): PaymentAccount[] {
    return FALLBACK;
  },
};
